import type { ActionSource, ClientItem, ClientMessage } from 'owa-mail-store';
import {
    getSelectedTableView,
    shouldSuppressServerMarkReadOnReplyOrForward,
    getIsTableItemDataPinned,
    MailRowDataPropertyGetter,
    tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault,
} from 'owa-mail-list-store';
import {
    lazyAddSafeSenderFromReadingPane,
    lazyArchiveMailSingleItem,
    lazyBlockLastSenderFromReadingPane,
    lazyDeleteItems,
    lazyMarkItemJunkNotJunkFromReadingPane,
    lazyMessageRecall,
    lazyReportItems,
    lazyPinUnpinItems,
    lazyScheduleSingleItem,
    lazyIgnoreStopIgnoreSingleItem,
} from 'owa-mail-triage-action';
import {
    lazyForwardItem,
    lazyForwardItemAsAttachment,
    lazyReplyToItem,
    lazyResendMessage,
    lazyNewMessage,
} from 'owa-mail-message-actions';
import { lazyPrint, lazyReplyByMeeting } from 'owa-mail-reading-pane-store';
import {
    onForward,
    onForwardAsAttachment,
    onReply,
    onReplyAll,
    onReplyWithMeeting,
    onCopilotReplyWithMeeting,
} from 'owa-mail-commands/lib/actions/onResponse';
import type { ClientItemId } from 'owa-client-ids';
import type FlagType from 'owa-service/lib/contract/FlagType';
import type { MailboxInfo } from 'owa-client-types';
import { type MenuItemType } from 'owa-filterable-menu/lib/components/MenuItemType';
import type { ReadOnlyRibbonControlDefProps } from 'owa-mail-ribbon-utils';
import type RetentionTagType from 'owa-service/lib/contract/RetentionTagType';
import closePopout from 'owa-popout-v2/lib/utils/closePopout';
import { isPopout } from 'owa-popout-v2';
import { getActionSourceFromCommandingViewMode } from 'owa-command-ribbon';
import { getCurrentGroupInformationStore } from 'owa-groups-shared-store/lib/CurrentGroupInformationStore';
import getDefaultDisposalType from 'owa-mail-reading-pane-store/lib/utils/getDefaultDisposalType';
import { getGroupInformation } from 'owa-groups-shared-store/lib/selectors/getGroupInformation';
import { getMailMenuItemShouldShow } from 'owa-mail-filterable-menu-behavior';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { getRibbonMailboxInfo } from 'owa-mail-ribbon-utils/lib/getRibbonMailboxInfo';
import { isReportPhishingSupportedForUnifiedInboxSelection } from 'owa-mail-ribbon-utils/lib/isReportPhishingSupportedForUnifiedInboxSelection';
import { isGlobalFeatureEnabled } from 'owa-feature-flags/lib/utils/isFeatureEnabled';
import { isGroupSelected } from 'owa-group-utils';
import { lazyCreateRuleFromItem } from 'owa-mail-inbox-rules';
import { lazyMarkItemAsReadFromReadingPane } from 'owa-mail-mark-read-actions';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { lazyOnAssignPolicy } from 'owa-mail-retention-policies';
import { lazyOpenRulesPanelInModal } from 'owa-groups-inbox-rules-option';
import onArchive from 'owa-mail-commands/lib/actions/onArchive';
import onBlock from 'owa-mail-commands/lib/actions/onBlock';
import onCreateRule from 'owa-mail-commands/lib/actions/onCreateRule';
import onDelete from 'owa-mail-commands/lib/actions/onDelete';
import { onFlagUnflag } from 'owa-mail-commands/lib/actions/onFlagUnflag';
import onJunkNotJunk from 'owa-mail-commands/lib/actions/onJunkNotJunk';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import onMarkAsPhishing from 'owa-mail-commands/lib/actions/onMarkAsPhishing';
import onMarkAsReadUnread from 'owa-mail-commands/lib/actions/onMarkAsReadUnread';
import onNeverBlock from 'owa-mail-commands/lib/actions/onNeverBlock';
import onPrint from 'owa-mail-commands/lib/actions/onPrint';
import onShowInImmersiveReader from 'owa-mail-commands/lib/actions/onShowInImmersiveReader';
import { onShowInImmersiveReader as onShowInImmersiveReaderSingleItem } from 'owa-immersive-reader-store/lib/utils/onShowInImmersiveReader';
import { getAccountScopeUserSettings } from 'owa-session-store/lib/selectors/getAccountScopeUserSettings';
import renderZoomButtonsCallout from './renderZoomButtonsCallout';
import { type MailRibbonControlId } from 'owa-ribbon-ids/lib/mailRibbonId';
import { lazyShowReadReceiptsDialog } from 'owa-track-read-receipts';
import { ReportType } from 'owa-reporting';
import { lazyChatAroundEmail, lazyShareEmailToTeamsChat } from 'owa-teams-integration';
import { getSelectedTeamsActionItem } from './getSelectedTeamsActionContext';
import { getSingleSelectedItemFromView } from './getSelectedItemFromView';
import type EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import type { AttachmentFileAttributes } from 'owa-attachment-file-types';
import { lazyCreateMailResponseBodyContent } from 'owa-mail-content-utils';
import { lazyGetRespondSubject } from 'owa-mail-compose-actions';
import { type ComposeOperation } from 'owa-mail-compose-store';
import convertLocalItemAttachmentsToFileAttributes from 'owa-mail-file-support/lib/utils/convertLocalItemAttachmentsToFileAttributes';
import { getLocalFileMessage } from 'owa-mail-file-support/lib/utils/localFileMessageStore';
import createLocalFileAttachment from 'owa-mail-file-support/lib/utils/createLocalFileAttachment';
import { isMessageFileMailboxInfo } from 'owa-client-types/lib/MessageFileMailboxInfo';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import getTabById from 'owa-tab-store/lib/utils/getTabById';
import { type SecondaryReadingPaneTabViewState, TabType } from 'owa-tab-store';
import { type CommandingViewActionSource } from 'owa-analytics-types';
import { logUsage } from 'owa-analytics';
import type { AppButtonOnExecuteParameter } from 'owa-acui';
import { messageExtensionsRibbonButtonOnExecute } from 'owa-message-extension-flyout/lib/utils/messageExtensionsRibbonButtonOnExecute';
import {
    isNoItemContext,
    isMultipleSelection,
} from 'owa-message-extension-flyout/lib/utils/messageExtensionItemContext';
import { isSharedReadItemCheckForAddins } from 'owa-mail-store/lib/utils/sharedFolderUtilsForAddins';
import onSweep from 'owa-mail-commands/lib/actions/onSweep';
import onPinUnpin from 'owa-mail-commands/lib/actions/onPinUnpin';
import onSnooze from 'owa-mail-commands/lib/actions/onSnooze';
import type { OwaDate } from 'owa-datetime';
import onIgnoreStopIgnore from 'owa-mail-commands/lib/actions/onIgnoreStopIgnore';
import { lazyOpenCollaborationProtectionAgent } from 'owa-mail-protection';
import {
    MessageAnnotationSource,
    type QueryAnnotation,
    QueryAnnotationType,
} from '@1js/sydney-types';
import { trace } from 'owa-trace';
import tryCloseSecondaryReadingPaneTab from 'owa-tab-store/lib/utils/tryCloseSecondaryReadingPaneTab';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';

export const onArchiveClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    if (itemId) {
        lazyArchiveMailSingleItem.import().then(archiveMailSingleItem => {
            const targetWindow = windowRetriever(tabId);
            archiveMailSingleItem(itemId.Id).then(() => {
                closePopout(targetWindow);
            });
        });
    } else {
        onArchive(getActionSourceFromCommandingViewMode());
        tryCloseSecondaryReadingPaneTab();
    }
};

export const onBlockClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();
    if (itemId) {
        const targetWindow = windowRetriever(tabId);
        lazyBlockLastSenderFromReadingPane
            .importAndExecute(
                itemId.Id,
                itemId.mailboxInfo,
                [] /* instrumentationContext */,
                actionSource,
                targetWindow
            )
            .then(blockAttempted => {
                if (blockAttempted) {
                    closePopout(targetWindow);
                }
            });
    } else {
        onBlock(actionSource, blockAttempted => {
            if (blockAttempted) {
                tryCloseSecondaryReadingPaneTab();
            }
        });
    }
};

export const onDeleteClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    const targetWindow = windowRetriever(tabId);
    // We should check both popout and deeplink scenarios
    const isPopoutOrDeepLink = isPopout(targetWindow);
    if (itemId) {
        lazyDeleteItems.import().then(deleteItems => {
            deleteItems(
                [itemId.Id],
                getDefaultDisposalType(itemId.Id),
                [] /* instrumentationContext */,
                getActionSourceFromCommandingViewMode(),
                itemId.mailboxInfo,
                targetWindow,
                isPopoutOrDeepLink
            ).then(() => {
                if (isPopoutOrDeepLink) {
                    closePopout(targetWindow);
                }
            });
        });
    } else {
        onDelete(
            getActionSourceFromCommandingViewMode() /* actionSourceMailStore */,
            getActionSourceFromCommandingViewMode() /* actionSourceAnalyticsActions */,
            undefined /* isExplicitSoftDelete */,
            targetWindow
        );
        tryCloseSecondaryReadingPaneTab();
    }
};

export const onFlagUnflagClicked = (
    flagType: FlagType | undefined,
    override: boolean | undefined,
    itemId: ClientItemId | undefined
): void => {
    onFlagUnflag(getActionSourceFromCommandingViewMode(), flagType, override, itemId);
};

export const onClickSweep = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    mailboxInfo: MailboxInfo,
    itemId?: string
): void => {
    const targetWindow = windowRetriever(tabId);
    onSweep(mailboxInfo, targetWindow, itemId, 'PopoutRibbon' /* source */);
};

export const onForwardClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();
    if (itemId) {
        const targetWindow = windowRetriever(tabId);
        const isLocalItem =
            isFileActivationCachedTab(tabId) || isMessageFileMailboxInfo(itemId.mailboxInfo);

        if (isLocalItem) {
            handleLocalForwardItem(itemId, actionSource, targetWindow);
        } else {
            forwardItem(itemId, actionSource, targetWindow);
        }
    } else {
        onForward(actionSource);
    }
};

export const onMarkAsPhishingClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();
    if (itemId) {
        const targetWindow = windowRetriever(tabId);
        lazyReportItems
            .importAndExecute(
                ReportType.Phishing,
                [itemId.Id],
                getSelectedTableView(),
                actionSource,
                targetWindow
            )
            .then(reported => {
                if (reported) {
                    closePopout(targetWindow);
                }
            });
    } else {
        onMarkAsPhishing(actionSource, reportAttempted => {
            if (reportAttempted) {
                tryCloseSecondaryReadingPaneTab();
            }
        });
    }
};

export const onMarkAsReadUnreadClicked = (itemId: ClientItemId | undefined): void => {
    const actionSource = getActionSourceFromCommandingViewMode();
    if (itemId) {
        const item: ClientMessage = getMailStore().items.get(itemId.Id) as ClientMessage;
        lazyMarkItemAsReadFromReadingPane.importAndExecute(
            itemId.Id,
            getSelectedTableView(),
            !item?.IsRead /* isReadValue */,
            [] /* instrumentationContext */,
            actionSource
        );
    } else {
        onMarkAsReadUnread(actionSource);
    }
};

export const onPrintClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();
    if (itemId) {
        const targetWindow = windowRetriever(tabId);
        lazyPrint.importAndExecute(
            {
                mailboxInfo: itemId.mailboxInfo,
                Id: itemId.Id,
            },
            actionSource,
            targetWindow
        );
    } else {
        onPrint(actionSource);
    }
};

export const onReplyAllClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();

    if (itemId) {
        const targetWindow = windowRetriever(tabId);
        const isLocalItem =
            isFileActivationCachedTab(tabId) || isMessageFileMailboxInfo(itemId.mailboxInfo);

        // If the item is local and the tab is a file activation tab, we need to create a new message instead of ReplyAll since the item
        // does not exist on the server
        if (isLocalItem) {
            const item = getMailStore().items.get(itemId.Id) as ClientMessage | undefined;
            if (!item) {
                return;
            }
            const fromRecipient: EmailAddressWrapper = {
                Name: item.From?.Mailbox?.Name,
                EmailAddress: item.From?.Mailbox?.EmailAddress,
            };
            const toRecipients: EmailAddressWrapper[] = [
                fromRecipient,
                ...(item.ToRecipients || []),
            ];
            invokeNewMessage(item, 2, actionSource, targetWindow, toRecipients, item.CcRecipients);
        } else {
            lazyReplyToItem.importAndExecute(
                itemId,
                true /** isReplyAll */,
                actionSource,
                [] /* instrumentationContext */,
                !!shouldSuppressServerMarkReadOnReplyOrForward(getSelectedTableView()),
                targetWindow
            );
        }
    } else {
        onReplyAll(actionSource);
    }
};

export const onReplyClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();
    if (itemId) {
        const targetWindow = windowRetriever(tabId);
        const isLocalItem =
            isFileActivationCachedTab(tabId) || isMessageFileMailboxInfo(itemId.mailboxInfo);

        // If the item is local and the tab is a file activation tab, we need to create a new message instead of Reply since the item
        // does not exist on the server
        if (isLocalItem) {
            const item = getMailStore().items.get(itemId.Id) as ClientMessage | undefined;
            if (!item) {
                return;
            }
            const toRecipients: EmailAddressWrapper[] = [
                {
                    Name: item.From?.Mailbox?.Name,
                    EmailAddress: item.From?.Mailbox?.EmailAddress,
                },
            ];
            invokeNewMessage(item, 1, actionSource, targetWindow, toRecipients);
        } else {
            lazyReplyToItem.importAndExecute(
                itemId,
                false /** isReplyAll */,
                actionSource,
                [] /* instrumentationContext */,
                !!shouldSuppressServerMarkReadOnReplyOrForward(getSelectedTableView()),
                targetWindow
            );
        }
    } else {
        onReply(actionSource);
    }
};

export const onReplyWithMeetingClicked = (
    mailboxInfo: MailboxInfo,
    itemId: ClientItemId | undefined
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();

    logUsage('ReplyWithMeeting_Clicked', mailboxInfo, { actionSource });
    if (itemId) {
        lazyReplyByMeeting.importAndExecute(
            itemId.Id,
            false /* isCopilot */,
            undefined /*calendarInlineComposeViewState*/,
            actionSource
        );
    } else {
        onReplyWithMeeting(actionSource);
    }
};

export const onCopilotReplyWithMeetingClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    mailboxInfo: MailboxInfo,
    itemId: ClientItemId | undefined
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();

    logUsage('CopilotReplyWithMeeting_Clicked', mailboxInfo, { actionSource });
    if (itemId) {
        const targetWindow = windowRetriever(tabId);
        lazyReplyByMeeting.importAndExecute(
            itemId.Id,
            true /* isCopilot */,
            undefined /*calendarInlineComposeViewState*/,
            actionSource,
            undefined /*extractionEntityId*/,
            undefined /*internetMessageId*/,
            undefined /*sigsData*/,
            undefined /*eventTimestamp*/,
            undefined /*mailboxInfo*/,
            targetWindow
        );
    } else {
        onCopilotReplyWithMeeting(actionSource);
    }
};

export const onShowInImmersiveReaderClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();
    if (itemId) {
        const targetWindow = windowRetriever(tabId);
        onShowInImmersiveReaderSingleItem(
            itemId /* itemId */,
            actionSource,
            undefined,
            targetWindow
        );
    } else {
        onShowInImmersiveReader(actionSource);
    }
};

export const onCreateRuleClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    const source = getActionSourceFromCommandingViewMode();
    if (itemId) {
        const item: ClientMessage = getMailStore().items.get(itemId.Id) as ClientMessage;
        lazyCreateRuleFromItem.import().then(createRuleFromItem => {
            const targetWindow = windowRetriever(tabId);
            createRuleFromItem(item, source, targetWindow);
        });
    } else {
        onCreateRule(source);
    }
};

export const onManageRulesClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    const tableView = getSelectedTableView(true /* shouldGetEffectiveTableView */);
    if (!tableView) {
        return;
    }
    let mailboxInfo: MailboxInfo | undefined;
    try {
        mailboxInfo =
            itemId?.mailboxInfo ??
            tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault(
                getMailboxInfo(tableView),
                [...tableView.selectedRowKeys.keys()],
                tableView
            );
    } catch (_error) {
        // If the UIB helper throws, it means that the selected items belong to different mailboxes
        // Fall back to the global settings account
        mailboxInfo = getGlobalSettingsAccountMailboxInfo();
    }

    if (isGlobalFeatureEnabled('grp-loadFolders') && isGroupSelected()) {
        const group = getGroupInformation(
            mailboxInfo,
            getCurrentGroupInformationStore().smtpAddress
        );

        const groupName = group?.basicInformation?.DisplayName;

        if (groupName) {
            lazyOpenRulesPanelInModal.importAndExecute(groupName);
        }
    } else {
        const targetWindow = windowRetriever(tabId);
        lazyMountAndShowFullOptions.importAndExecute(
            'mail',
            'rules',
            undefined,
            targetWindow,
            mailboxInfo
        );
    }
};

export const onReportSplitButtonClicked = (
    props: ReadOnlyRibbonControlDefProps,
    windowRetriever: (tabId: string | undefined) => Window
): void => {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);

    /**
     * For unified inbox, the primary action mirrors the non-unified behavior:
     * "Report phishing" when the selected account supports it, otherwise "Junk / Not Junk".
     */
    if (isUnifiedInboxMailboxInfo(mailboxInfo)) {
        if (isReportPhishingSupportedForUnifiedInboxSelection(props)) {
            onMarkAsPhishingClicked(props.projectionTabId, windowRetriever, props.itemId);
        } else {
            onJunkNotJunkClicked(props, windowRetriever, mailboxInfo);
        }
        return;
    }

    const isCloudCache =
        /* eslint-disable-next-line no-restricted-properties  -- (https://aka.ms/OWALintWiki)
         * Adding IsShadowMailbox to restricted properties/methods.
         *	> 'IsShadowMailbox' is restricted from being used. IsCloudCache/IsShadowMailbox should be resolved in ECS as a filter in a feature flight if possible. */
        !!getAccountScopeUserSettings(mailboxInfo)?.SessionSettings?.IsShadowMailbox;

    /**
     * We don't expose "Report phishing" for cloud cache accounts, so we need to
     * change default action to "Report junk" in that scenario.
     */
    if (isCloudCache) {
        onJunkNotJunkClicked(props, windowRetriever, mailboxInfo);
    } else {
        onMarkAsPhishingClicked(props.projectionTabId, windowRetriever, props.itemId);
    }
};

export const onJunkNotJunkClicked = (
    props: ReadOnlyRibbonControlDefProps,
    windowRetriever: (tabId: string | undefined) => Window,
    mailboxInfo: MailboxInfo
): void => {
    const { projectionTabId: tabId, itemId } = props;
    const actionSource = getActionSourceFromCommandingViewMode();
    if (itemId) {
        const targetWindow = windowRetriever(tabId);
        lazyMarkItemJunkNotJunkFromReadingPane
            .importAndExecute(itemId.Id, getSelectedTableView(), actionSource, targetWindow)
            .then(isReportSuccessful => {
                if (isReportSuccessful) {
                    closePopout(targetWindow);
                }
            });
    } else {
        // For unified inbox, getMailMenuItemShouldShow(MarkAsJunk) always returns false
        // because isNativeReportingEnabled fails for the virtual UIB mailboxInfo.
        // Use folder-based check instead to determine junk vs not-junk.
        const isJunkValueToSet = isUnifiedInboxMailboxInfo(mailboxInfo)
            ? folderIdToName(getSelectedTableView()?.tableQuery?.folderId ?? '') !== 'junkemail'
            : getMailMenuItemShouldShow(24, mailboxInfo);
        void onJunkNotJunk(actionSource, isJunkValueToSet, reportAttempted => {
            if (reportAttempted) {
                tryCloseSecondaryReadingPaneTab();
            }
        });
    }
};

export const onForwardEmailAsAttachmentClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();
    if (itemId) {
        const targetWindow = windowRetriever(tabId);
        const isLocalItem =
            isFileActivationCachedTab(tabId) || isMessageFileMailboxInfo(itemId.mailboxInfo);

        // If the item is local and the tab is a file activation tab, we need to create a new message instead of Forward as attachment
        // since the item does not exist on the server
        if (isLocalItem) {
            const item: ClientMessage = getMailStore().items.get(itemId.Id) as ClientMessage;
            // Locally parsed files only exist in-memory with a synthetic item id, so
            // attach the original file bytes instead of a mail item reference.
            const originalFile = getLocalFileMessage(itemId.Id);
            void invokeNewMessage(
                item,
                3,
                actionSource,
                targetWindow,
                undefined /* toEmailAddressWrappers */,
                undefined /* ccEmailAddressWrappers */,
                true /* attachBody */,
                originalFile ? createLocalFileAttachment(originalFile) : undefined
            );
        } else {
            lazyForwardItemAsAttachment.importAndExecute(itemId, actionSource, targetWindow);
        }
    } else {
        onForwardAsAttachment(actionSource);
    }
};

export const onNeverBlockClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    itemId: ClientItemId | undefined
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();
    if (itemId) {
        const targetWindow = windowRetriever(tabId);
        lazyAddSafeSenderFromReadingPane.importAndExecute(
            itemId.Id,
            actionSource,
            itemId.mailboxInfo,
            targetWindow
        );
    } else {
        onNeverBlock(actionSource, safeSenderAdded => {
            if (safeSenderAdded) {
                tryCloseSecondaryReadingPaneTab();
            }
        });
    }
};

export const onMoreRetentionPoliciesClicked = (mailboxInfo?: MailboxInfo): void => {
    lazyMountAndShowFullOptions.importAndExecute(
        'mail',
        'retentionPolicies',
        undefined /* option */,
        undefined /* targetWindow */,
        mailboxInfo
    );
};

export const onAssignArchivePolicyClicked = (
    retentionId: string,
    isArchivePolicyClicked: boolean,
    selectedRetentionTagType: RetentionTagType | null | RetentionTagType[],
    folderId: string | null,
    clientItem: ClientItem | undefined,
    actionSource: ActionSource,
    targetWindow: Window | undefined
): void => {
    lazyOnAssignPolicy.import().then(onAssignPolicy => {
        onAssignPolicy(
            retentionId,
            isArchivePolicyClicked,
            selectedRetentionTagType,
            folderId,
            clientItem,
            actionSource,
            targetWindow
        );
    });
};

export const onMessageRecallClicked = (
    itemId: ClientItemId | undefined,
    mailboxInfo: MailboxInfo,
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();
    const targetWindow = windowRetriever(tabId);
    lazyMessageRecall.importAndExecute(itemId?.Id, actionSource, mailboxInfo, targetWindow);
};

export const onZoomClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    mailboxInfo: MailboxInfo | undefined
): void => {
    const targetWindow = windowRetriever(tabId);
    renderZoomButtonsCallout((688).toString(), targetWindow, mailboxInfo);
};

export const onTrackReadReceiptsClicked = (
    mailboxInfo: MailboxInfo,
    itemId: ClientItemId | undefined,
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window
): void => {
    const actionSource = getActionSourceFromCommandingViewMode();
    const targetWindow = windowRetriever(tabId);
    lazyShowReadReceiptsDialog.importAndExecute(
        itemId?.Id,
        actionSource,
        mailboxInfo,
        targetWindow
    );
};

export const onChatAroundEmailButtonsClicked = (
    isSLR: boolean,
    itemId: ClientItemId | undefined,
    mailboxInfo: MailboxInfo,
    senderOnly: boolean
): void => {
    const message = getSelectedTeamsActionItem(itemId);

    if (message) {
        // Prefer the item's own MailboxInfo to correctly route cross-account and multi-account
        // scenarios. Fall back to the ribbon-provided mailboxInfo for MessageFile items (e.g. .msg
        // files opened locally) since those have a synthetic mailbox not tied to any real account.
        const effectiveMailboxInfo =
            message.MailboxInfo && !isMessageFileMailboxInfo(message.MailboxInfo)
                ? message.MailboxInfo
                : mailboxInfo;
        lazyChatAroundEmail.importAndExecute(
            senderOnly,
            message,
            effectiveMailboxInfo,
            'chat',
            isSLR ? 'SimplifiedRibbon' : 'MultiLineRibbon'
        );
    }
};

export const onMessageResendClicked = (
    itemId: ClientItemId | undefined,
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window
): void => {
    const message: ClientItem | undefined = !!itemId
        ? getMailStore().items.get(itemId.Id)
        : getSingleSelectedItemFromView(getSelectedTableView());

    if (message?.ItemId) {
        const targetWindow = windowRetriever(tabId);
        lazyResendMessage.importAndExecute(
            {
                ...message.ItemId,
                mailboxInfo: message.MailboxInfo,
            },
            targetWindow
        );
    }
};

export const onShareToTeamsButtonClicked = (
    itemId: ClientItemId | undefined,
    mailboxInfo: MailboxInfo
): void => {
    const message = getSelectedTeamsActionItem(itemId);

    if (message) {
        // Prefer the item's own MailboxInfo to correctly route cross-account and multi-account
        // scenarios. Fall back to the ribbon-provided mailboxInfo for MessageFile items (e.g. .msg
        // files opened locally) since those have a synthetic mailbox not tied to any real account.
        const effectiveMailboxInfo =
            message.MailboxInfo && !isMessageFileMailboxInfo(message.MailboxInfo)
                ? message.MailboxInfo
                : mailboxInfo;
        lazyShareEmailToTeamsChat.importAndExecute(message, effectiveMailboxInfo);
    }
};

// Synchronously checks if a tab is a file activation tab (opened from a local file).
// This avoids async lazy imports that can fail in release builds due to code splitting.
function isFileActivationCachedTab(tabId?: string): boolean {
    if (!tabId) {
        return false;
    }
    const tab = getTabById(tabId);
    if (tab?.type !== TabType.SecondaryReadingPane) {
        return false;
    }
    const tabData = (tab as SecondaryReadingPaneTabViewState).data;
    return !!tabData?.isFileActivation;
}

// This function is used to forward an item from the server
function forwardItem(
    itemId: ClientItemId,
    actionSource: CommandingViewActionSource,
    targetWindow: Window
) {
    lazyForwardItem.importAndExecute(
        itemId,
        actionSource,
        [] /* instrumentationContext */,
        !!shouldSuppressServerMarkReadOnReplyOrForward(getSelectedTableView()),
        targetWindow
    );
}

// This function is used to forward an item that was created locally e.g. opening a message from a file
function handleLocalForwardItem(
    itemId: ClientItemId,
    actionSource: CommandingViewActionSource,
    targetWindow: Window
) {
    const item = getMailStore().items.get(itemId.Id) as ClientMessage | undefined;
    if (!item) {
        return;
    }

    // If the item has attachments, we need to create a copy of the item and forward the copy to ensure the attachments are included
    if (item.Attachments && item.Attachments.length > 0) {
        void handleLocalForwardItemWithAttachments(item, actionSource, targetWindow);
    } else {
        // If the item does not have attachments, we can just forward the original item
        void invokeNewMessage(item, 3, actionSource, targetWindow);
    }
}

// This function is used to forward an item that was created locally and has attachments
async function handleLocalForwardItemWithAttachments(
    item: ClientMessage,
    actionSource: CommandingViewActionSource,
    targetWindow: Window
): Promise<void> {
    try {
        const attachmentFiles = await convertLocalItemAttachmentsToFileAttributes(
            item.Attachments ?? []
        );
        await invokeNewMessage(
            item,
            3,
            actionSource,
            targetWindow,
            undefined,
            undefined,
            undefined,
            attachmentFiles
        );
    } catch {
        try {
            await invokeNewMessage(item, 3, actionSource, targetWindow);
        } catch {
            // Best-effort fallback to avoid leaking an unhandled rejection back to the click handler.
        }
    }
}

// This function is used to create a new message from a local item with the appropriate subject,
// quoted body, and optional uploaded attachments.
async function invokeNewMessage(
    item: ClientMessage,
    operation: ComposeOperation,
    actionSource: ActionSource,
    targetWindow: Window,
    toEmailAddressWrappers?: EmailAddressWrapper[],
    ccEmailAddressWrappers?: EmailAddressWrapper[],
    attachBody?: boolean,
    attachmentFiles?: AttachmentFileAttributes[]
) {
    // Get the subject with appropriate prefix
    const getRespondSubject = await lazyGetRespondSubject.import();
    const subject = getRespondSubject(item, operation);

    // Get the quoted body which includes header information and separator, then call NewMessage
    const createMailResponseBodyContent = await lazyCreateMailResponseBodyContent.import();
    const quotedBody = !attachBody
        ? createMailResponseBodyContent(item, true /* isHtml */, subject)
        : undefined;

    // For MessageFile items (e.g. EML), use the module context mailbox info instead of
    // the MessageFile mailbox info, since compose requires a real mailbox context
    const moduleMailboxInfo = getModuleContextMailboxInfo();
    const mailboxInfo =
        isMessageFileMailboxInfo(item.MailboxInfo) && moduleMailboxInfo
            ? moduleMailboxInfo
            : item.MailboxInfo;

    lazyNewMessage.importAndExecute(actionSource, {
        mailboxInfo,
        subject,
        toEmailAddressWrappers,
        ccEmailAddressWrappers,
        body: quotedBody,
        bodyType: 'HTML',
        attachmentFiles,
        // When attachmentFiles is provided, skip mailItems entirely — the
        // in-memory item has no server id, so a mail-item reference would fail.
        // Otherwise, reference the item to carry over attachBody or existing attachments.
        mailItems:
            !attachmentFiles && (attachBody || (item.Attachments && item.Attachments.length > 0))
                ? [item]
                : undefined,
        isPopout: false,
        targetWindow,
        // Local items (EML / MessageFile) only exist client-side. Mark as file-activation
        // and override the operation so signature, telemetry and DAB see the actual
        // Reply / ReplyAll / Forward operation while the save goes through CreateItem.
        isFileActivation: true,
        operationOverride: operation,
    });
}

export const onMessageExtensionsFlyoutButtonClicked = (
    params: AppButtonOnExecuteParameter,
    itemId: ClientItemId | undefined,
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    mailboxInfo?: MailboxInfo
): void => {
    const item: ClientItem | undefined = !!itemId
        ? getMailStore().items.get(itemId.Id)
        : getSingleSelectedItemFromView(getSelectedTableView());

    const targetWindow = windowRetriever(tabId);

    const messageExtensionType = isNoItemContext()
        ? 'NoItemContext'
        : isMultipleSelection(isPopout(targetWindow))
        ? 'MultipleSelection'
        : item && isSharedReadItemCheckForAddins(item, mailboxInfo ?? item?.MailboxInfo)
        ? 'ReadSharedFolder'
        : 'Read';

    messageExtensionsRibbonButtonOnExecute(
        params,
        targetWindow,
        '' /* formId */,
        messageExtensionType,
        mailboxInfo,
        itemId?.Id ?? item?.ItemId?.Id /* readingPaneItemId */
    );
};

export const onPinUnpinClicked = (
    itemId: ClientItemId | undefined,
    mailboxInfo: MailboxInfo
): void => {
    if (itemId) {
        const item: ClientItem | undefined = getMailStore().items.get(itemId.Id);
        if (item) {
            const isPinned = getIsTableItemDataPinned(item);
            lazyPinUnpinItems.importAndExecute(
                [itemId.Id],
                mailboxInfo,
                !isPinned /*isPinnedToSet*/,
                undefined /*tableView*/,
                undefined /*conversationIds*/
            );
        }
    } else {
        onPinUnpin(getActionSourceFromCommandingViewMode());
        tryCloseSecondaryReadingPaneTab();
    }
};

export const onSnoozeClicked = (
    date: OwaDate | undefined,
    itemId: ClientItemId | undefined
): void => {
    if (itemId) {
        const item = getMailStore().items.get(itemId.Id);
        const mailboxInfo = item?.MailboxInfo;
        const parentFolderId = item?.ParentFolderId?.Id;
        if (mailboxInfo && parentFolderId) {
            lazyScheduleSingleItem
                .import()
                .then(scheduleItem =>
                    scheduleItem(
                        itemId.Id,
                        mailboxInfo,
                        parentFolderId,
                        date,
                        getSelectedTableView()
                    )
                );
        }
    } else {
        onSnooze(date, getActionSourceFromCommandingViewMode());
        tryCloseSecondaryReadingPaneTab();
    }
};

export const onIgnoreStopIgnoreClicked = (
    props: ReadOnlyRibbonControlDefProps,
    windowRetriever: (tabId: string | undefined) => Window
): void => {
    if (props.itemId) {
        const targetWindow = windowRetriever(props.projectionTabId);
        lazyIgnoreStopIgnoreSingleItem.importAndExecute(props.itemId.Id, targetWindow).then(() => {
            closePopout(targetWindow);
        });
    } else {
        const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
        onIgnoreStopIgnore(
            getActionSourceFromCommandingViewMode(),
            getMailMenuItemShouldShow(20, mailboxInfo)
        );
        tryCloseSecondaryReadingPaneTab();
    }
};

export const onEvaluateMessageSafeClicked = (
    props: ReadOnlyRibbonControlDefProps,
    windowRetriever: (tabId: string | undefined) => Window
): void => {
    const { itemId } = props;
    const tableView = getSelectedTableView();
    const targetWindow = windowRetriever(props.projectionTabId);
    const queryAnnotations: QueryAnnotation[] = [];
    let subject = '';
    let senderName = '';
    let senderEmail = '';
    let timestamp = '';
    let messageItemId = '';
    let internetMessageId = '';

    if (itemId) {
        const item: ClientMessage | undefined = getMailStore().items.get(itemId.Id) as
            | ClientMessage
            | undefined;
        subject = item?.Subject ?? '';
        senderName = item?.From?.Mailbox?.Name ?? '';
        senderEmail = item?.From?.Mailbox?.EmailAddress ?? '';
        timestamp = item?.DateTimeReceived ?? '';
        messageItemId = itemId.Id;
        internetMessageId = item?.InternetMessageId ?? '';
        queryAnnotations.push({
            queryAnnotationType: QueryAnnotationType.Message,
            id: itemId.Id,
            text: subject,
            messageAnnotationSource: MessageAnnotationSource.ClientAnnotated,
        });
    } else {
        const selectedRowKeys = tableView ? [...tableView.selectedRowKeys.keys()] : [];
        const itemIds = tableView
            ? MailRowDataPropertyGetter.getItemIds(selectedRowKeys[0], tableView)
            : [];
        subject =
            tableView && selectedRowKeys.length > 0
                ? MailRowDataPropertyGetter.getSubject(selectedRowKeys[0], tableView)
                : '';
        if (tableView && selectedRowKeys.length > 0) {
            const senderMailbox = MailRowDataPropertyGetter.getLastSenderMailbox(
                selectedRowKeys[0],
                tableView
            );
            senderName = senderMailbox?.Name ?? '';
            senderEmail = senderMailbox?.EmailAddress ?? '';
            timestamp = MailRowDataPropertyGetter.getLastDeliveryTimeStamp(
                selectedRowKeys[0],
                tableView
            );
            messageItemId =
                MailRowDataPropertyGetter.getItemIds(selectedRowKeys[0], tableView)[0] ?? '';
            internetMessageId = MailRowDataPropertyGetter.getInternetMessageId(
                selectedRowKeys[0],
                tableView
            );
        }
        queryAnnotations.push(
            ...itemIds.map(currentItemId => ({
                queryAnnotationType: QueryAnnotationType.Message,
                id: currentItemId,
                text: subject,
                messageAnnotationSource: MessageAnnotationSource.ClientAnnotated,
            }))
        );
    }

    lazyOpenCollaborationProtectionAgent
        .importAndExecute({
            message: {
                itemId: messageItemId,
                subject,
                senderName,
                senderEmail,
                timestamp,
                internetMessageId,
            },
            source: 'Report_RibbonButton',
            targetWindow,
            queryAnnotations,
            mailboxInfo: getRibbonMailboxInfo(props.mailboxInfo),
        })
        .catch((error: Error) => {
            trace.warn(error.message);
        });
};
